// PackageEditDialog.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  TextField,
  MenuItem,
  Switch,
  Button,
  Typography,
  Slide,
  Box,
  Avatar,
  InputAdornment,
} from "@mui/material";
import { GetAgentList } from "api/agent";
import { CloseOutlined, EditOutlined } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import {
  getPhotoUrl,
  getScanText,
  playAlertSound,
  playCorrectSound,
  playIncorrectSound,
  TRACKING_MAX_LENGTH,
} from "utils";
import { GetCustomersList } from "api/customers";
import { GetRouteList, GetRouteDetail } from "api/routes";
import { isMobile } from "react-device-detect";
import { deletePackagePhoto, PatchPackage, uploadPackagePhoto } from "api/packages";
import { SubmitPackageEditRequest, UploadPackageChangeRequestEvidence } from "api/packageChangeRequest";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { user, defaultStation } from "localStorage";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoPreviewDialog from "./PhotoPreviewDialog";
import moment from "moment";
import PhotoCaptureDialog from "./PhotoCaptureDialog";
import { getFileExtension } from 'utils/fileUtils'; // Add this import
import { sectionLabelSx, fieldSx, accentFieldSx, switchSx, ToggleCard } from "components/bento/BentoFormControls";
import {
  BG_TINT,
  BORDER_PURPLE,
  BG_PURPLE_GRADIENT,
  RADIUS_CARD,
  RADIUS_TILE,
  DIVIDER,
  PURPLE_MAIN,
  PURPLE_DARK,
  TEXT_DARK,
  TEXT_BODY,
  MONO,
} from "components/bento/bentoTokens";

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

// Transition component for dialog animations
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// This dialog forces its own zIndex above the default modal layer (see Dialog sx below,
// for the nested-dialog case). A <TextField select>'s dropdown menu is a separately
// portaled Popover that still uses the default theme.zIndex.modal unless told otherwise,
// which then renders BEHIND this dialog — the field looks unresponsive because the menu
// opens invisibly under the dialog paper. Every select in this dialog needs its menu
// pushed above that override.
const selectMenuProps = { sx: { zIndex: (theme) => theme.zIndex.modal + 20 } };

// Define the props for the PackageEditDialog
const PackageEditDialog = ({
  open,
  onClose,
  packageData,
  onSuccess = () => { }, // New prop for success callback
  rows = [],
  setRows = () => { },
}) => {
  // Package data
  const [packageId, setPackageId] = useState("");
  const [tracking, setTracking] = useState("");
  const [trackingError, setTrackingError] = useState("");
  const [description, setDescription] = useState("");
  const [orderId, setOrderId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [routeId, setRouteId] = useState("");
  const [customer, setCustomer] = useState(null);
  const [shippingFee, setShippingFee] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [status, setStatus] = useState("");
  const [station, setStation] = useState("");
  const [whatsappStatus, setWhatsappStatus] = useState(false);
  const [remark, setRemark] = useState("");
  const [amount, setAmount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [isDeliveryRequired, setIsDeliveryRequired] = useState(false);
  const [isCOD, setIsCOD] = useState(false);
  const [isSubCustomer, setIsSubCustomer] = useState(false);
  const [level, setLevel] = useState("1");
  const [agentList, setAgentList] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [isForwardRequired, setIsForwardRequired] = useState(false);
  const [isSelfOrder, setIsSelfOrder] = useState(false);
  const [transitCheckRequired, setTransitCheckRequired] = useState(false);
  const [finalCheckRequired, setFinalCheckRequired] = useState(false);
  const [finalShippingFee, setFinalShippingFee] = useState(0);
  const [agentWhatsappStatus, setAgentWhatsappStatus] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [forwardAddress, setForwardAddress] = useState(null);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [forwardAddresses, setForwardAddresses] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [pendingTracking, setPendingTracking] = useState(null);
  const [location, setLocation] = useState({ lat: null, lng: null });

  // Snapshot of whether the package originally had a value for these fields —
  // set once per packageData load, used to make them required-if-was-set on save.
  const hadCustomerRef = useRef(false);
  const hadRouteRef = useRef(false);
  const hadStationRef = useRef(false);

  // Guards the one-time "snap station to its packageData value" sync below so it
  // only applies once per packageData load — not every time stationList refetches
  // because the admin picked a different Route (which would otherwise fight their edit).
  const initialStationSyncDoneRef = useRef(false);

  // Snapshot of the original field values — used on submit to send only fields that
  // actually changed, so untouched fields (which may be genuinely unset) never get
  // round-tripped back to the backend as the literal string "undefined".
  const originalRef = useRef({});

  // Paths of already-saved photos the user removed in this session — the actual
  // delete only happens on Save, so closing without saving leaves them untouched.
  const pendingDeletePathsRef = useRef([]);

  // Front-end data
  const [searchCustomer, setSearchCustomer] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const searchCustomerTimeout = useRef(null); // Ref to hold the timeout ID
  const [routeList, setRouteList] = useState([]);
  const [stationList, setStationList] = useState(null);
  const [isNewCustomerSelected, setIsNewCustomerSelected] = useState(false);
  const _user = user();
  const isAgentUser = _user.role === "agent";
  // Only admin can save an edit directly — every other role's save goes through
  // /api/package-change-request instead and waits for admin approval. See
  // CLAUDE.md's package-change-request feature notes / backend/routes/packageChangeRequest.js.
  const canEditDirectly = _user.role === "admin";
  const { t } = useTranslation();

  // Tracking validation function
  const validateTracking = (value) => {
    if (!value || value.length < 8) {
      setTrackingError(`Tracking number must be at least 8 characters long. Current: ${value ? value.length : 0} characters`);
      return false;
    }
    setTrackingError("");
    return true;
  };

  // Handle tracking change with validation
  const handleTrackingChange = (e) => {
    const value = e.target.value.toUpperCase(); // Convert to uppercase like backend
    setTracking(value);
    validateTracking(value);
  };

  // Reset all states to initial values
  const resetAllStates = () => {
    // Discard any unsaved photo staging — revoke local previews and drop pending deletes.
    photos.forEach((p) => {
      if (p.isNew && p.path) URL.revokeObjectURL(p.path);
    });
    pendingDeletePathsRef.current = [];

    // Package data states
    setPackageId("");
    setTracking("");
    setTrackingError("");
    setDescription("");
    setOrderId("");
    setQuantity(1);
    setRouteId("");
    setCustomer(null);
    setShippingFee(0);
    setStatus("");
    setStation("");
    setWhatsappStatus(false);
    setRemark("");
    setAmount(0);
    setPaymentStatus(false);
    setIsDeliveryRequired(false);
    setIsCOD(false);
    setIsSubCustomer(false);
    setLevel("1");
    setAgentId("");
    setIsForwardRequired(false);
    setIsSelfOrder(false);
    setFinalShippingFee(0);
    setAgentWhatsappStatus(false);
    setDeliveryAddress(null);
    setForwardAddress(null);
    setDeliveryAddresses([]);
    setForwardAddresses([]);
    setPhotos([]);
    setPhotoViewerOpen(false);
    setPhotoIndex(0);
    setPhotoDialogOpen(false);
    setPendingTracking(null);
    setLocation({ lat: null, lng: null });

    // Frontend data states
    setSearchCustomer("");
    setCustomerList([]);
    setStationList(null);
    setIsNewCustomerSelected(false);
    setIsPhotoUploading(false);

    // Clear search timeout if it exists
    if (searchCustomerTimeout.current) {
      clearTimeout(searchCustomerTimeout.current);
      searchCustomerTimeout.current = null;
    }
  };

  // Handle dialog close with state reset
  const handleDialogClose = () => {
    resetAllStates();
    onClose();
  };

  // Set initial photos from packageData
  useEffect(() => {
    if (packageData?.photos) {
      setPhotos(
        packageData.photos.map((photo) =>
          typeof photo === "string"
            ? { path: photo, isNew: false }
            : { ...photo, isNew: false }
        )
      );
    } else {
      setPhotos([]);
    }
  }, [packageData]);

  // Reused for the upload/delete work that now happens at Save time (was previously
  // "is this photo capture uploading" — now "is Save persisting staged photo changes").
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  // Stage the captured photo locally — it's only actually uploaded when the user
  // clicks Save, so closing the dialog without saving discards it entirely.
  const handlePhotoCapture = (file) => {
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setPhotos((prev) => [
      ...prev,
      {
        isNew: true,
        file,
        path: previewUrl,
        lat: location.lat || "",
        lng: location.lng || "",
        timestamp: new Date().toISOString(),
      },
    ]);
    setPhotoDialogOpen(false);
    setPendingTracking(null);
  };

  const handleFormSubmited = async (e) => {
    e.preventDefault();

    // Validate tracking before submission
    if (!validateTracking(tracking)) {
      Swal.fire({
        icon: "error",
        title: "ข้อมูลไม่ถูกต้อง",
        text: "กรุณาแก้ไขข้อผิดพลาดของหมายเลขติดตามก่อนบันทึก",
      });
      return;
    }

    // Customer/Route/Station are only required if the package originally had a value
    if (hadCustomerRef.current && !customer) {
      Swal.fire({
        icon: "error",
        title: "ข้อมูลไม่ถูกต้อง",
        text: "กรุณาเลือกลูกค้า",
      });
      return;
    }
    if (hadRouteRef.current && (!routeId || routeId === "N/A")) {
      Swal.fire({
        icon: "error",
        title: "ข้อมูลไม่ถูกต้อง",
        text: "กรุณาเลือกเส้นทาง",
      });
      return;
    }
    if (hadStationRef.current && !station) {
      Swal.fire({
        icon: "error",
        title: "ข้อมูลไม่ถูกต้อง",
        text: "กรุณาเลือกสถานี",
      });
      return;
    }

    const customerId = customer ? customer._id : "";

    // Only send fields that actually changed from the original package data — an
    // untouched field may genuinely be unset, and sending it anyway (as "" or the
    // stringified "undefined") would overwrite it with junk on the backend.
    const normalize = (v) => {
      if (v === null || v === undefined) return "";
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    };
    const changedFields = {};
    const noteIfChanged = (key, currentVal) => {
      if (normalize(currentVal) === normalize(originalRef.current[key])) return;
      changedFields[key] = currentVal;
    };

    noteIfChanged("tracking", tracking);
    noteIfChanged("description", description);
    noteIfChanged("orderId", orderId);
    noteIfChanged("customerId", customerId);
    noteIfChanged("amount", amount);
    noteIfChanged("quantity", quantity);
    noteIfChanged("shippingFee", shippingFee);
    noteIfChanged("deliveryFee", deliveryFee);
    noteIfChanged("routeId", routeId);
    noteIfChanged("station", station);
    noteIfChanged("status", status);
    noteIfChanged("remark", remark);
    noteIfChanged("paymentStatus", paymentStatus);
    noteIfChanged("whatsappStatus", whatsappStatus);
    noteIfChanged("isCOD", isCOD);
    noteIfChanged("isDeliveryRequired", isDeliveryRequired);
    noteIfChanged("isForwardRequired", isForwardRequired);
    noteIfChanged("isSubCustomer", isSubCustomer);
    noteIfChanged("agentId", agentId);
    noteIfChanged("level", level);
    noteIfChanged("isSelfOrder", isSelfOrder);
    noteIfChanged("transitCheckRequired", transitCheckRequired);
    noteIfChanged("finalCheckRequired", finalCheckRequired);
    noteIfChanged("finalShippingFee", finalShippingFee);
    noteIfChanged("agentWhatsappStatus", agentWhatsappStatus);
    noteIfChanged("forwardAddress", forwardAddress);
    noteIfChanged("deliveryAddress", deliveryAddress);

    if (!canEditDirectly) {
      // Manager (or any role without direct edit access) — submit for admin
      // approval instead of saving. Newly added photos ride along as the
      // request's evidence (POST /:id/evidence) and are copied onto the package
      // when an admin approves; removing saved photos stays admin-only.
      const newPhotos = photos.filter((p) => p.isNew && p.file);
      if (Object.keys(changedFields).length === 0 && newPhotos.length === 0) {
        Swal.fire({ icon: "info", title: t("packageChangeRequest.noChangesTitle"), text: t("packageChangeRequest.noChangesText") });
        return;
      }
      const { value: reason } = await Swal.fire({
        title: t("packageChangeRequest.reasonPromptTitle"),
        input: "textarea",
        inputPlaceholder: t("packageChangeRequest.reasonPlaceholder"),
        showCancelButton: true,
        confirmButtonText: t("packageChangeRequest.submitButton"),
        cancelButtonText: t("common.cancel"),
        inputValidator: (value) => (!value || !value.trim() ? t("packageChangeRequest.reasonRequired") : undefined),
      });
      if (!reason) return;

      const res = await SubmitPackageEditRequest(
        packageId,
        newPhotos.length > 0 ? { ...changedFields, withPhotos: true } : changedFields,
        reason
      );
      if (res.success && newPhotos.length > 0) {
        setIsPhotoUploading(true);
        let photosFailed = false;
        // The evidence route takes at most 5 files per call.
        for (let i = 0; i < newPhotos.length; i += 5) {
          const up = await UploadPackageChangeRequestEvidence(res.data._id, newPhotos.slice(i, i + 5).map((p) => p.file));
          if (!up?.success) photosFailed = true;
        }
        setIsPhotoUploading(false);
        if (photosFailed) {
          handleDialogClose();
          playAlertSound();
          Swal.fire({ title: t("packageChangeRequest.submittedTitle"), icon: "warning", text: t("packageChangeRequest.photosFailedText") });
          return;
        }
      }
      if (res.success) {
        handleDialogClose();
        playCorrectSound();
        Swal.fire({
          title: t("packageChangeRequest.submittedTitle"),
          icon: "success",
          text: t("packageChangeRequest.submittedText"),
          timer: 2000,
        });
      } else {
        playAlertSound();
        Swal.fire({ title: t("packageChangeRequest.errorTitle"), icon: "error", text: res.message });
      }
      return;
    }

    const formData = new FormData();
    formData.append("_id", packageId);
    Object.entries(changedFields).forEach(([key, val]) => {
      const outVal = val && typeof val === "object" ? JSON.stringify(val) : (val ?? "");
      formData.append(key, outVal);
    });

    // Commit staged photo changes now — uploads/deletes only happen at Save time.
    // Use the pre-edit tracking number so this still resolves the package correctly
    // even if the user renamed the tracking number in this same Save.
    const photoTracking = originalRef.current.tracking || tracking;
    const newPhotosToUpload = photos.filter((p) => p.isNew && p.file);
    const pathsToDelete = pendingDeletePathsRef.current;

    if (newPhotosToUpload.length > 0 || pathsToDelete.length > 0) {
      setIsPhotoUploading(true);
      try {
        for (const photo of newPhotosToUpload) {
          const ext = getFileExtension(photo.file);
          await uploadPackagePhoto(photoTracking, photo.file, ext, { lat: photo.lat, lng: photo.lng }, undefined, undefined, defaultStation()?.id);
          URL.revokeObjectURL(photo.path);
        }
        for (const path of pathsToDelete) {
          await deletePackagePhoto(photoTracking, path);
        }
        pendingDeletePathsRef.current = [];
      } catch (err) {
        setIsPhotoUploading(false);
        playIncorrectSound();
        Swal.fire({
          icon: "error",
          title: "อัปโหลด/ลบรูปไม่สำเร็จ",
          text: err.message || "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ",
        });
        return;
      }
      setIsPhotoUploading(false);
    }

    PatchPackage(formData).then((res) => {
      if (res.status === 200) {
        const updatedRows = rows.map((pkg) =>
          pkg._id === packageId
            ? {
              ...pkg,
              tracking,
              description,
              orderId,
              customer,
              amount,
              quantity,
              shippingFee,
              deliveryFee,
              routeId,
              station,
              status,
              remark,
              paymentStatus,
              whatsappStatus,
              isCOD,
              isDeliveryRequired,
              isForwardRequired,
              isSubCustomer,
              agentId,
              level,
              isSelfOrder,
              transitCheckRequired,
              finalCheckRequired,
              finalShippingFee,
              forwardAddress,
              deliveryAddress,
              photos: res.data?.photos ?? photos,
            }
            : pkg
        );
        setRows(updatedRows);
        handleDialogClose();
        playCorrectSound();
        onSuccess(); // Call the success callback
        Swal.fire({
          title: "อัปเดตสำเร็จ",
          icon: "success",
          text: "ข้อมูลพัสดุได้รับการอัปเดตเรียบร้อยแล้ว",
          timer: 1500,
        });
      } else if (res.status === 400) {
        playAlertSound();
        Swal.fire({
          title: "เกิดข้อผิดพลาด",
          icon: "error",
          text: `เกิดข้อผิดพลาด: ${res.data?.message || res.message}`,
        });
      } else {
        playIncorrectSound();
        Swal.fire({
          title: "เกิดข้อผิดพลาด",
          icon: "error",
          text: `เกิดข้อผิดพลาด: ${res.message}`,
        });
      }
    });
  };

  // Set defaults when a new customer is selected
  useEffect(() => {
    // Set delivery & forward addresses from customer
    if (customer) {
      if ("forwardAddresses" in customer)
        setForwardAddresses(customer.forwardAddresses);
      if ("deliveryAddresses" in customer)
        setDeliveryAddresses(customer.deliveryAddresses);
    }

    if (isNewCustomerSelected && customer) {
      if ("default_route" in customer) setRouteId(customer.default_route);
      if ("isCOD" in customer) setIsCOD(customer.isCOD);
      if ("isDeliveryRequired" in customer)
        setIsDeliveryRequired(customer.isDeliveryRequired);
      if ("isForwardRequired" in customer)
        setIsForwardRequired(customer.isForwardRequired);
      if ("isSubCustomer" in customer) setIsSubCustomer(customer.isSubCustomer);
      if ("isSelfOrder" in customer) setIsSelfOrder(customer.isSelfOrder);
      if ("agentId" in customer) setAgentId(customer.agentId);
      if ("level" in customer) setLevel(customer.level);

      setIsNewCustomerSelected(false);
    }
  }, [isNewCustomerSelected, customer]);

  useEffect(() => {
    if (packageData?.forwardAddress && forwardAddresses.length > 0) {
      const matchAddress = forwardAddresses.find(
        (address) => address.id === packageData.forwardAddress.id
      );
      // Only update if we found a match and current address is different
      if (matchAddress && (!forwardAddress || forwardAddress.id !== matchAddress.id)) {
        setForwardAddress(matchAddress);
      }
    }
    // Don't reset to null when forwardAddresses is empty if we already have address from packageData
  }, [forwardAddresses, packageData, forwardAddress]);

  useEffect(() => {
    if (packageData?.deliveryAddress && deliveryAddresses.length > 0) {
      const matchAddress = deliveryAddresses.find(
        (address) => address.id === packageData.deliveryAddress.id
      );
      // Only update if we found a match and current address is different
      if (matchAddress && (!deliveryAddress || deliveryAddress.id !== matchAddress.id)) {
        setDeliveryAddress(matchAddress);
      }
    }
    // Don't reset to null when deliveryAddresses is empty if we already have address from packageData
  }, [packageData, deliveryAddresses, deliveryAddress]);

  useEffect(() => {
    // Only run once per packageData load — otherwise every stationList refetch caused
    // by the admin picking a different Route would snap 'station' back to the
    // package's original value and make Route/Station edits look like they don't stick.
    if (initialStationSyncDoneRef.current) return;

    if (packageData?.station && stationList?.length > 0) {
      const matchStation = stationList.find(
        (station) => station.station === packageData.station
      );

      // Only update station if we found a match and current station is different
      if (matchStation && station !== matchStation.station) {
        setStation(matchStation.station);
      }
      initialStationSyncDoneRef.current = true;
    }
    // Don't reset station to empty when stationList is empty if we already have station from packageData
    // This prevents clearing the station while the list is still loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationList, packageData]); // Intentionally excluding 'station' to prevent resetting user selections

  useEffect(() => {
    if (packageData?.customer && customerList?.length > 0) {
      const matchCustomer = customerList.find(
        (customer) => customer._id === packageData.customer._id
      );
      // Only update customer if we found a match and current customer is different
      if (matchCustomer && (!customer || customer._id !== matchCustomer._id)) {
        setCustomer(matchCustomer);
      }
    }
    // Don't reset customer to null when customerList is empty if we already have customer from packageData
    // This prevents clearing the customer while the list is still loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageData]); // Intentionally excluding 'customerList' and 'customer' to prevent circular dependencies

  // Fetch route list
  useEffect(() => {
    GetRouteList().then((res) => {
      if (res.data) {
        setRouteList(res.data);
      }
    }).catch((error) => {
      console.error("Error loading route list:", error);
    });
  }, []);

  // Set state value
  useEffect(() => {
    if (packageData) {
      const {
        _id,
        tracking,
        description,
        customer,
        orderId,
        amount,
        quantity,
        shippingFee,
        deliveryFee,
        routeId,
        status,
        remark,
        paymentStatus,
        whatsappStatus,
        isCOD,
        isDeliveryRequired,
        isForwardRequired,
        isSubCustomer,
        agentId,
        level,
        isSelfOrder,
        transitCheckRequired,
        finalCheckRequired,
        finalShippingFee,
        agentWhatsappStatus,
      } = packageData;

      // Set customer directly from packageData first
      setCustomer(customer || null);

      // Set station directly from packageData first
      setStation(packageData.station || "");

      // Snapshot "had value" flags from the original package data
      hadCustomerRef.current = !!customer;
      hadRouteRef.current = !!(routeId && routeId !== "N/A");
      hadStationRef.current = !!packageData.station;
      initialStationSyncDoneRef.current = false;

      originalRef.current = {
        tracking,
        description,
        orderId,
        customerId: customer ? customer._id : "",
        amount,
        quantity,
        shippingFee,
        deliveryFee,
        routeId,
        station: packageData.station,
        status,
        remark,
        paymentStatus,
        whatsappStatus,
        isCOD,
        isDeliveryRequired,
        isForwardRequired,
        isSubCustomer,
        agentId,
        level,
        isSelfOrder,
        transitCheckRequired,
        finalCheckRequired,
        finalShippingFee,
        agentWhatsappStatus,
        forwardAddress: packageData.forwardAddress || null,
        deliveryAddress: packageData.deliveryAddress || null,
      };

      // Set addresses directly from packageData first
      setDeliveryAddress(packageData.deliveryAddress || null);
      setForwardAddress(packageData.forwardAddress || null);

      // Set search customer to trigger API call for customer list
      setSearchCustomer(customer && customer.whatsapp);
      setPackageId(_id);
      setTracking(tracking);
      // Validate the tracking number from packageData
      validateTracking(tracking);
      setDescription(description);
      setOrderId(orderId);
      setAmount(amount);
      setQuantity(quantity);
      setShippingFee(shippingFee);
      setDeliveryFee(deliveryFee || 0);
      setStatus(status);
      setRemark(remark);
      setPaymentStatus(paymentStatus);
      setWhatsappStatus(whatsappStatus);
      setRouteId(routeId);
      setIsCOD(isCOD);
      setIsDeliveryRequired(isDeliveryRequired);
      setIsForwardRequired(isForwardRequired);
      setIsSubCustomer(isSubCustomer);
      setAgentId(agentId);
      setLevel(level);
      setIsSelfOrder(isSelfOrder);
      setTransitCheckRequired(transitCheckRequired || false);
      setFinalCheckRequired(finalCheckRequired || false);
      setFinalShippingFee(finalShippingFee ?? 0);
      setAgentWhatsappStatus(agentWhatsappStatus);
    }
  }, [packageData]);

  // Fetch station list based on route selection
  useEffect(() => {
    if (routeId && routeId !== "" && routeId !== "N/A") {
      GetRouteDetail(routeId).then((res) => {
        if (res.status === 200 && res.data && res.data.navigator) {
          setStationList(res.data.navigator);
        } else if (res.success && res.data && res.data.navigator) {
          // Handle direct backend response format (fallback)
          setStationList(res.data.navigator);
        } else {
          console.error("Route detail error:", res.message || res);
          setStationList([]);
        }
      }).catch((error) => {
        console.error("Error loading route detail:", error);
        setStationList([]);
      });
    } else {
      setStationList(null);
    }
  }, [routeId]);

  useEffect(() => {
    if (searchCustomer && searchCustomer !== "") {
      // Clear any existing timeout
      if (searchCustomerTimeout.current) {
        clearTimeout(searchCustomerTimeout.current);
      }

      // Set a new timeout
      searchCustomerTimeout.current = setTimeout(() => {
        GetCustomersList(searchCustomer).then((res) => {
          if (res && res.success) {
            setCustomerList(res.data);
          } else {
            console.error(res.message);
          }
        });
      }, 500); // 500ms delay
    } else {
      setCustomerList([]); // Clear customer list if searchCustomer is empty
    }

    // Cleanup function to clear the timeout if the component unmounts or searchCustomer changes
    return () => {
      if (searchCustomerTimeout.current) {
        clearTimeout(searchCustomerTimeout.current);
      }
    };
  }, [searchCustomer]);

  // Fetch agent list for sub-customers
  useEffect(() => {
    if (isSubCustomer) {
      GetAgentList().then((res) => {
        if (res.data) setAgentList(res.data);
      });
    } else {
      setAgentId("");
    }
  }, [isSubCustomer]);

  // Stage photo removal by index — the actual delete (for already-saved photos) only
  // happens on Save, so closing the dialog without saving leaves the photo untouched.
  const handleDeletePhoto = (idx) => {
    Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: "รูปจะถูกลบออกเมื่อกดบันทึกข้อมูล",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ใช่, ลบเลย!",
      cancelButtonText: "ไม่, เก็บไว้"
    }).then((result) => {
      if (result.isConfirmed) {
        const photoToDelete = photos[idx];
        if (photoToDelete?.isNew) {
          // Never uploaded — just drop the local staged photo, nothing to delete server-side.
          if (photoToDelete.path) URL.revokeObjectURL(photoToDelete.path);
        } else if (photoToDelete?.path) {
          // Already-saved photo — stage the delete for Save time.
          pendingDeletePathsRef.current.push(photoToDelete.path);
        }
        setPhotos((prev) => prev.filter((_, i) => i !== idx));
        setPhotoViewerOpen(false);
      }
    });
  }

  // Open photo viewer
  const handleOpenPhotoViewer = (idx) => {
    setPhotoIndex(idx);
    setPhotoViewerOpen(true);
  };

  // Close photo viewer
  const handleClosePhotoViewer = () => {
    setPhotoViewerOpen(false);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      keepMounted
      onClose={handleDialogClose}
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{ sx: { borderRadius: RADIUS_CARD, overflow: "hidden" } }}
      // This dialog is opened as a nested "edit" action from within other list
      // dialogs (e.g. PendingArrivalDialog) — `keepMounted` above makes MUI
      // register it in the modal stack at first render rather than at open
      // time, so its automatic z-index can end up BELOW a dialog opened later.
      // Force it above the default modal layer so it's always the topmost.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      // MUI's FocusTrap only cooperates with other MUI-portalled overlays (Select
      // menus, nested MUI Dialogs). SweetAlert2 (the manager-approval reason prompt
      // in handleFormSubmited) renders straight into document.body outside that
      // stack, so without this the trap yanks keyboard focus back into this Dialog
      // on every keystroke and the Swal textarea becomes untypeable.
      disableEnforceFocus
    >
      <form onSubmit={handleFormSubmited}>
        <DialogTitle sx={{ p: "24px 28px 20px" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: "12px", flexShrink: 0,
                  background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <EditOutlined sx={{ fontSize: 19, color: PURPLE_MAIN }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "20px", fontWeight: 800, color: TEXT_DARK, letterSpacing: "-.01em" }}>
                  Edit Package
                </Typography>
                {tracking && (
                  <Typography sx={{ fontFamily: MONO, fontSize: "13px", fontWeight: 600, color: PURPLE_DARK, mt: "2px" }}>
                    {tracking}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton aria-label="close" onClick={handleDialogClose}>
              <CloseOutlined />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflowY: "auto", maxHeight: isMobile ? "calc(100vh - 200px)" : "calc(100vh - 220px)" }}>

          {/* Section 1: Customer & Route */}
          <Box sx={{ p: "4px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
            <Box sx={sectionLabelSx}>Customer &amp; Route</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "16px", mb: "16px" }}>
              <Autocomplete
                onChange={(e, value) => {
                  setCustomer(value || null);
                  // Clear search when customer is cleared
                  if (!value) {
                    setSearchCustomer("");
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue.trim() !== "" && newInputValue.length >= 2) {
                    setSearchCustomer(newInputValue);
                  }
                }}
                disablePortal
                id="combo-box-customer"
                options={customerList}
                value={customer}
                isOptionEqualToValue={(option, value) =>
                  value && value.id === option.id
                } // Custom equality check
                getOptionLabel={(option) =>
                  `${option.name} - ${option.whatsapp}` || ""
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: "11px",
                        fontWeight: 700,
                        background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
                      }}
                    >
                      {initials(option.name)}
                    </Avatar>
                    <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: TEXT_BODY }}>
                      {option.name}{option.whatsapp ? ` · ${option.whatsapp}` : ""}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    required={hadCustomerRef.current}
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: customer ? (
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: "11px",
                            fontWeight: 700,
                            background: "linear-gradient(135deg,#7c3aed,#4c1d95)",
                            mr: "2px",
                          }}
                        >
                          {initials(customer.name)}
                        </Avatar>
                      ) : params.InputProps.startAdornment,
                    }}
                  />
                )}
              />

              <TextField
                select
                label="Route"
                id="route"
                value={(!routeId || routeId === "N/A") ? "" : routeId}
                onChange={(event) => setRouteId(event.target.value)}
                required={hadRouteRef.current}
                sx={fieldSx}
                SelectProps={{ MenuProps: selectMenuProps }}
              >
                {routeList &&
                  routeList.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {id} ({name})
                    </MenuItem>
                  ))}
              </TextField>

              <TextField
                select
                label="Station"
                fullWidth
                value={station || ""}
                onChange={(e) => setStation(e.target.value)}
                required={hadStationRef.current}
                sx={fieldSx}
                SelectProps={{ MenuProps: selectMenuProps }}
              >
                {stationList && stationList.length > 0 ? (
                  stationList.map(({ _id, station, stationName, nextStationName }) => (
                    <MenuItem key={_id} value={station}>
                      {stationName} {" => "} {nextStationName || ""}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    {routeId ? "Loading stations..." : "Select route first"}
                  </MenuItem>
                )}
              </TextField>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "12px" }}>
              <ToggleCard
                label="COD"
                sub="ເກັບຄ່າບໍລິການປາຍທາງ"
                checked={isCOD || false}
                onChange={() => setIsCOD(!isCOD)}
              />

              <ToggleCard
                label="ລູກຄ້າສັ່ງເຄື່ອງເອງ"
                sub="Self-order"
                checked={isSelfOrder || false}
                onChange={() => setIsSelfOrder(!isSelfOrder)}
              />

              {!isAgentUser && (
                <ToggleCard
                  label="ລູກຄ້າຂອງຕົວແທນ"
                  sub="Sub-customer order"
                  checked={isSubCustomer || false}
                  onChange={() => setIsSubCustomer(!isSubCustomer)}
                >
                  {isSubCustomer && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ fontSize: "12px", fontWeight: 600, color: TEXT_BODY }}>
                          ສົ່ງແຈ້ງຕົວແທນແລ້ວ — {agentWhatsappStatus ? "ສົ່ງແລ້ວ" : "ຍັງບໍ່ໄດ້ສົ່ງ"}
                        </Box>
                        <Switch
                          checked={agentWhatsappStatus || false}
                          onChange={() => setAgentWhatsappStatus(!agentWhatsappStatus)}
                          sx={switchSx}
                        />
                      </Box>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="ເລືອກຕົວແທນ Agent"
                        value={agentId || ""}
                        required
                        sx={accentFieldSx}
                        onChange={(event) => setAgentId(event.target.value)}
                        SelectProps={{ MenuProps: selectMenuProps }}
                      >
                        {agentList &&
                          agentList.map(({ id, name }) => (
                            <MenuItem key={id} value={id}>
                              {id} - {name}
                            </MenuItem>
                          ))}
                      </TextField>
                      <TextField
                        type="number"
                        fullWidth
                        size="small"
                        label="Final Shipping Fee"
                        value={finalShippingFee}
                        sx={accentFieldSx}
                        onChange={(e) => setFinalShippingFee(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start" sx={{ color: PURPLE_DARK }}>฿</InputAdornment> }}
                      />
                    </Box>
                  )}
                </ToggleCard>
              )}
            </Box>
          </Box>

          {/* Section 2: Delivery Options */}
          <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
            <Box sx={sectionLabelSx}>Delivery Options</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "12px" }}>
              <ToggleCard
                label="Home Delivery"
                sub="ຈັດສົ່ງສິນຄ້າຮອດເຮືອນລູກຄ້າ"
                checked={isDeliveryRequired || false}
                disabled={!customer}
                onChange={() => setIsDeliveryRequired(!isDeliveryRequired)}
              >
                {isDeliveryRequired && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="ເລືອກທີ່ຢູ່"
                    value={deliveryAddress?.id || ""}
                    required
                    sx={accentFieldSx}
                    onChange={(event) => {
                      const selectedId = event.target.value;
                      const selectedAddress =
                        deliveryAddresses.find((address) => address.id === selectedId) || null;
                      setDeliveryAddress(selectedAddress);
                    }}
                    SelectProps={{ MenuProps: selectMenuProps }}
                  >
                    {deliveryAddresses &&
                      deliveryAddresses.map((address) => (
                        <MenuItem key={address.id} value={address.id}>
                          {address.title} - {address.description}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </ToggleCard>

              <ToggleCard
                label="ຝາກນຳຂົນສົ່ງຕໍ່"
                sub="ເຊັ່ນ ຮຸ່ງອາລຸນ, ອະນຸສິດ, ມີໄຊ ..."
                checked={isForwardRequired || false}
                disabled={!customer}
                onChange={() => setIsForwardRequired(!isForwardRequired)}
              >
                {isForwardRequired && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="ເລືອກທີ່ຢູ່"
                    value={forwardAddress?.id || ""}
                    required
                    sx={accentFieldSx}
                    onChange={(event) => {
                      const selectedId = event.target.value;
                      const selectedAddress =
                        forwardAddresses.find((address) => address.id === selectedId) || null;
                      setForwardAddress(selectedAddress);
                    }}
                    SelectProps={{ MenuProps: selectMenuProps }}
                  >
                    {forwardAddresses &&
                      forwardAddresses.map((address) => (
                        <MenuItem key={address.id} value={address.id}>
                          {address.logisticCompany} - {address.branch} {address.description}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </ToggleCard>
            </Box>
          </Box>

          {/* Section 3: Status */}
          <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
            <Box sx={sectionLabelSx}>Status</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "12px" }}>
              <ToggleCard
                label="Whatsapp Status"
                sub={whatsappStatus ? "ສົ່ງແລ້ວ" : "ຍັງບໍ່ໄດ້ສົ່ງ"}
                checked={whatsappStatus || false}
                onChange={() => setWhatsappStatus(!whatsappStatus)}
              />
              <ToggleCard
                label="Delivery Status"
                sub={paymentStatus ? "ສົ່ງແລ້ວ" : "ຍັງບໍ່ໄດ້ສົ່ງ"}
                checked={paymentStatus || false}
                onChange={() => setPaymentStatus(!paymentStatus)}
              />
              <ToggleCard
                label="Fragile check — Transit"
                sub="ຕ້ອງແນບຮູບກ່ອນສະແກນຜ່ານສະຖານີປ່ຽນຖ່າຍ"
                checked={transitCheckRequired || false}
                onChange={() => setTransitCheckRequired(!transitCheckRequired)}
              />
              <ToggleCard
                label="Fragile check — Final station"
                sub="ຕ້ອງແນບຮູບກ່ອນສະແກນຜ່ານສະຖານີປາຍທາງ"
                checked={finalCheckRequired || false}
                onChange={() => setFinalCheckRequired(!finalCheckRequired)}
              />
            </Box>
          </Box>

          {/* Section 4: Package Details */}
          <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
            <Box sx={sectionLabelSx}>Package Details</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px", mb: "16px" }}>
              <TextField
                label="Tracking Number"
                id="tracking"
                value={tracking}
                required
                error={!!trackingError}
                helperText={trackingError}
                inputProps={{ maxLength: TRACKING_MAX_LENGTH }}
                sx={fieldSx}
                onChange={handleTrackingChange}
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    const finalValue = getScanText(tracking);
                    setTracking(finalValue);
                    validateTracking(finalValue);
                    document.getElementById("shippingFee").select();
                    document.getElementById("shippingFee").focus();
                    e.preventDefault();
                  }
                }}
              />

              <TextField
                label="Order ID"
                id="orderId"
                value={orderId}
                sx={fieldSx}
                onChange={(e) => setOrderId(e.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={5}
              label="Description"
              id="description"
              value={description}
              sx={{ ...fieldSx, mb: "16px" }}
              onChange={(e) => setDescription(e.target.value)}
            />

            <TextField
              fullWidth
              multiline
              maxRows={5}
              label="Remark"
              id="remark"
              value={remark}
              sx={fieldSx}
              onChange={(e) => setRemark(e.target.value)}
            />
          </Box>

          {/* Section 5: Pricing */}
          <Box sx={{ p: "22px 28px 20px", borderBottom: `1px solid ${DIVIDER}` }}>
            <Box sx={sectionLabelSx}>Pricing</Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "16px" }}>
              <TextField
                type="number"
                label="Amount"
                id="amount"
                value={amount}
                sx={fieldSx}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">฿</InputAdornment> }}
              />

              {isAgentUser ? (
                <TextField
                  type="number"
                  label="Final Shipping Fee"
                  id="finalShippingFee"
                  value={finalShippingFee}
                  sx={accentFieldSx}
                  onChange={(e) => setFinalShippingFee(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ color: PURPLE_DARK }}>฿</InputAdornment> }}
                />
              ) : (
                <TextField
                  type="number"
                  label="Shipping Fee"
                  id="shippingFee"
                  value={shippingFee}
                  disabled={isAgentUser}
                  sx={accentFieldSx}
                  onChange={(e) => setShippingFee(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ color: PURPLE_DARK }}>฿</InputAdornment> }}
                />
              )}

              <TextField
                type="number"
                label="Delivery Fee"
                id="deliveryFee"
                value={deliveryFee}
                sx={fieldSx}
                onChange={(e) => setDeliveryFee(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">฿</InputAdornment> }}
              />
            </Box>
          </Box>

          {/* Section 6: Photos */}
          <Box sx={{ p: "22px 28px 24px" }}>
            <Box sx={sectionLabelSx}>Photos</Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
              {photos.map((photo, idx) => (
                <Box
                  key={idx}
                  sx={{ position: "relative", display: "inline-block", cursor: "pointer" }}
                  onClick={() => handleOpenPhotoViewer(idx)}
                >
                  <Box
                    component="img"
                    src={getPhotoUrl(photo.preview || photo.path || photo)}
                    alt={`photo-${idx}`}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: "cover",
                      borderRadius: RADIUS_TILE,
                      border: `1px solid ${BORDER_PURPLE}`,
                    }}
                  />
                  {/* Show timestamp if available */}
                  {typeof photo === "object" && (
                    <Box sx={{ position: "absolute", bottom: 2, left: 2, right: 2, background: "rgba(0,0,0,0.55)", borderRadius: "8px", px: 0.5 }}>
                      {photo.timestamp && (
                        <Typography variant="caption" color="#fff" sx={{ display: "block", fontSize: "9.5px" }}>
                          {moment(photo.timestamp).format("DD/MM/YYYY HH:mm:ss")}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {(canEditDirectly || photo.isNew) && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeletePhoto(idx); }}
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        background: "#fff",
                        border: `1px solid ${BORDER_PURPLE}`,
                        width: 24,
                        height: 24,
                        "&:hover": { background: "#fdeef0" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
              <IconButton
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: RADIUS_TILE,
                  background: BG_PURPLE_GRADIENT,
                  border: `1.5px dashed ${BORDER_PURPLE}`,
                  color: PURPLE_MAIN,
                  "&:hover": { background: "#ede8fb" },
                }}
                onClick={() => {
                  setPhotoDialogOpen(true);
                  setPendingTracking(tracking);
                }}
              >
                <PhotoCamera sx={{ width: 28, height: 28 }} />
              </IconButton>
            </Box>
          </Box>

        </DialogContent>

        <DialogActions sx={{ p: "20px 28px", background: BG_TINT }}>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            disabled={isPhotoUploading}
            startIcon={<CheckIcon fontSize="small" />}
            sx={{
              background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`,
              boxShadow: "0 4px 16px -4px rgba(109,40,217,.55)",
              "&:hover": { background: `linear-gradient(135deg,#7c3aed,${PURPLE_DARK})`, opacity: 0.92 },
            }}
          >
            {canEditDirectly ? "Save" : t("packageChangeRequest.submitButton")}
          </Button>
        </DialogActions>
      </form>

      {/* Photo Viewer Dialog */}
      <PhotoPreviewDialog
        open={photoViewerOpen}
        photos={photos}
        photoIndex={photoIndex}
        onClose={handleClosePhotoViewer}
        onPrev={() => setPhotoIndex((photoIndex - 1 + photos.length) % photos.length)}
        onNext={() => setPhotoIndex((photoIndex + 1) % photos.length)}
      />
      <PhotoCaptureDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        onCapture={handlePhotoCapture}
        tracking={pendingTracking}
        location={location}
        setLocation={setLocation}
        isUploading={isPhotoUploading} // Add this prop
      />
    </Dialog>
  );
};

export default PackageEditDialog;
